"use client";

import React, { useEffect, useRef, Fragment, useState, useCallback } from "react";
import {
  ScrollShadow,
  ListBox,
  ListBoxItem,
  ListBoxSection,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownPopover,
  DropdownItem,
  Header,
  ListBoxProps,
  ListBoxSectionProps,
  Button,
  cn,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { ChatHistoryMetadata } from "@webuild-ai/admin/models/components";
import { useAuth } from "@webuild-ai/react-auth";

import { ModalOverlay, ModalHeader, ModalBody, ModalFooter } from "@/components/UI/ModalOverlay";
import { useChatHistoryStore } from "@/stores/ChatHistoryStore";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { RenameChatModal } from "@/components/Chat/RenameChatModal";
import { logger } from "@/telemetry";

interface ChatHistoryProps {
  isShowHistory: boolean;
  onNewChat: () => void;
  onSelectChat: (chatHistoryId: string) => void;
  itemClasses?: ListBoxProps<object>["itemClasses"];
  sectionClasses?: ListBoxSectionProps["classNames"];
  showScroll?: boolean;
}

export const ChatHistoryComponent: React.FC<ChatHistoryProps> = ({
  isShowHistory,
  onNewChat,
  onSelectChat,
  sectionClasses,
  showScroll = false,
}) => {
  const { user } = useAuth();
  const { adminClient } = useWebuildAPIClient();
  const listboxRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const hasScrolledOnLoadRef = useRef(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [chatToRename, setChatToRename] = useState<{ id: string; name: string } | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [chatToDelete, setChatToDelete] = useState<ChatHistoryMetadata | null>(null);
  const {
    displayedChats,
    chatHistory,
    selectedChatId,
    shouldScrollToSelected,
    clearScrollFlag,
    fetchChatHistory,
    loadMoreChatHistory,
    showMoreChats,
    hasMore,
    isLoading,
    deleteChatHistory,
  } = useChatHistoryStore();
  const { t } = useTranslation();

  const handleLoadMore = useCallback(async () => {
    if (adminClient && user?.id && !isLoadingMore) {
      setIsLoadingMore(true);

      try {
        // First try to show more from existing data
        const currentDisplayCount = displayedChats.length;
        const totalAvailable = chatHistory.length;

        if (currentDisplayCount < totalAvailable) {
          // Show more from existing data
          showMoreChats();
        } else if (hasMore) {
          // Fetch more from API
          await loadMoreChatHistory(adminClient);
        }
      } finally {
        setIsLoadingMore(false);
      }
    }
  }, [
    adminClient,
    user?.id,
    isLoadingMore,
    displayedChats.length,
    chatHistory.length,
    showMoreChats,
    hasMore,
    loadMoreChatHistory,
  ]);

  useEffect(() => {
    logger.info("user", { userId: user?.id });
    if (adminClient && user?.id) {
      fetchChatHistory(user.id, adminClient);
    }
  }, [adminClient, user, fetchChatHistory]);

  // Infinite scroll effect
  useEffect(() => {
    const scrollElement = scrollRef.current;

    if (!scrollElement) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollElement;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50; // 50px threshold for better UX

      if (
        isNearBottom &&
        (displayedChats.length < chatHistory.length || hasMore) &&
        !isLoadingMore &&
        !isLoading
      ) {
        logger.info("handleLoadMore");
        handleLoadMore();
      }
    };

    // Add throttling to prevent excessive calls
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    scrollElement.addEventListener("scroll", throttledHandleScroll, { passive: true });

    return () => scrollElement.removeEventListener("scroll", throttledHandleScroll);
  }, [
    displayedChats.length,
    chatHistory.length,
    hasMore,
    isLoadingMore,
    isLoading,
    handleLoadMore,
  ]);

  // Scroll to selected item on initial page load OR when shouldScrollToSelected flag is set
  // (e.g., after search selection)
  useEffect(() => {
    // Skip if already handled initial load scroll AND no explicit scroll request
    if (hasScrolledOnLoadRef.current && !shouldScrollToSelected) {
      return;
    }

    // Skip if no selection or no data
    if (!selectedChatId || displayedChats.length === 0) {
      return;
    }

    // Check if selected chat is in displayed list
    const isInDisplayed = displayedChats.some((chat) => chat.id === selectedChatId);

    if (!isInDisplayed) {
      // Chat not in list yet, wait for next update
      return;
    }

    // Mark initial load as complete - this prevents scroll on subsequent sidebar clicks
    hasScrolledOnLoadRef.current = true;

    // Clear the scroll flag if it was set (e.g., from search selection)
    if (shouldScrollToSelected) {
      clearScrollFlag();
    }

    const scrollToElement = () => {
      const container = scrollRef.current;
      const selectedElement = container?.querySelector(
        `[data-id="chat-history-item-${selectedChatId}"]`,
      ) as HTMLElement | null;

      if (selectedElement && container) {
        const containerRect = container.getBoundingClientRect();
        const elementRect = selectedElement.getBoundingClientRect();
        const offsetTop = elementRect.top - containerRect.top + container.scrollTop;
        const scrollTo = offsetTop - containerRect.height / 2 + elementRect.height / 2;

        container.scrollTo({ top: Math.max(0, scrollTo), behavior: "auto" });

        return true;
      }

      return false;
    };

    // Try immediately first
    if (scrollToElement()) {
      return;
    }

    // If element not found, use MutationObserver to wait for it
    const container = scrollRef.current;

    if (!container) return;

    const observer = new window.MutationObserver(() => {
      if (scrollToElement()) {
        observer.disconnect();
      }
    });

    observer.observe(container, { childList: true, subtree: true });

    // Cleanup after 3 seconds max
    const timeout = setTimeout(() => observer.disconnect(), 3000);

    return () => {
      observer.disconnect();
      clearTimeout(timeout);
    };
  }, [selectedChatId, displayedChats, shouldScrollToSelected, clearScrollFlag]);

  const handleChatSelect = (chatId: string) => {
    // Note: selectChat is called by the parent's onSelectChat handler
    // With selectionMode="none", the ListBox won't trigger any scroll behavior
    onSelectChat(chatId);
  };

  if (!isShowHistory) {
    return null;
  }

  const timeRanges = [
    { title: t("Today"), days: 0 },
    { title: t("Last 7 Days"), days: 7 },
    { title: t("Last 30 Days"), days: 30 },
    { title: t("Older"), days: Number.MAX_SAFE_INTEGER },
  ];

  // Helper to calculate calendar days difference (based on local midnight, not 24h periods)
  const getCalendarDaysDiff = (date: Date): number => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfItemDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffTime = startOfToday.getTime() - startOfItemDay.getTime();

    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  };

  return (
    <div
      className="relative flex flex-col w-full h-[500px] overflow-hidden px-1 pb-2 pt-0"
      data-id="chat-history-container"
    >
      {/* <Button
          fullWidth
          className="mb-6 mt-2 justify-start gap-3 bg-default-foreground px-3 text-default-50"
          startContent={
            <Icon className="text-default-50" icon="solar:chat-round-dots-linear" width={24} />
          }
          onPress={newChat}
          data-id="new-chat-button"
        >
          {t("New Chat")}
      </Button> */}
      <ScrollShadow
        ref={scrollRef}
        className={cn("overflow-y-auto flex-1 [&_*]:!scroll-margin-0", {
          "no-scrollbar ": !showScroll,
        })}
        data-id="chat-history-scroll"
        style={{ scrollBehavior: "auto", overflowAnchor: "none" }}
      >
        <ListBox
          ref={listboxRef}
          aria-label="Recent chats"
          className="min-h-full"
          data-id="chat-history-list"
          selectionMode="none"
          onAction={(key) => handleChatSelect(key as string)}
        >
          <Fragment>
            {timeRanges
              .map(({ title, days }) => {
                const filteredChats = displayedChats
                  .filter((item) => {
                    const itemDate = new Date(item.updatedAt);
                    const diffDays = getCalendarDaysDiff(itemDate);

                    // For "Today" (days=0), only include items from today (diffDays === 0)
                    // For other ranges, include items within the range
                    return days === 0 ? diffDays === 0 : diffDays <= days;
                  })
                  .filter((item) => {
                    // Only include items that haven't been shown in a more recent group
                    const itemDate = new Date(item.updatedAt);
                    const diffDays = getCalendarDaysDiff(itemDate);

                    // Check if this item belongs to a more recent group
                    const moreRecentGroup = timeRanges.find((range) => {
                      if (range.days >= days) return false;
                      // For "Today" range (days=0), check if diffDays is 0
                      if (range.days === 0) return diffDays === 0;

                      return diffDays <= range.days;
                    });

                    return !moreRecentGroup;
                  })
                  .sort(
                    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
                  );

                if (filteredChats.length === 0) return null;

                return (
                  <ListBoxSection
                    key={title}
                    className={cn("py-0", sectionClasses?.base)}
                    data-id={`chat-history-section-${days}`}
                  >
                    <Header className="text-xs font-bold text-default-400 px-3 py-1">
                      {title}
                    </Header>
                    {filteredChats.map((item: ChatHistoryMetadata) => (
                      <ListBoxItem
                        key={item.id}
                        data-hj-suppress
                        className={cn(
                          "group h-[38px] px-0.5 py-1.5 text-sm cursor-pointer relative scroll-mt-0 focus:scroll-mt-0",
                          selectedChatId === item.id
                            ? "bg-default-300 text-foreground font-medium dark:bg-default-200"
                            : "text-default-500",
                        )}
                        data-id={`chat-history-item-${item.id}`}
                        id={item.id}
                        textValue={item.summary || "Chat history item"}
                        onPress={() => handleChatSelect(item.id)}
                      >
                        <div className="flex items-center w-full">
                          <span className="truncate flex-1">{item.summary}</span>
                          <Dropdown>
                            <DropdownTrigger>
                              <button
                                aria-label={t("Chat actions")}
                                className="opacity-100 pointer-events-auto sm:opacity-0 sm:pointer-events-none sm:group-hover:opacity-100 sm:group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto transition-opacity min-w-5 w-5 h-5 flex items-center justify-center rounded-md hover:bg-default-100 shrink-0 ml-1 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                                type="button"
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                              >
                                <Icon
                                  className="text-default-500"
                                  icon="solar:menu-dots-bold"
                                  width={16}
                                />
                              </button>
                            </DropdownTrigger>
                            <DropdownPopover>
                              <DropdownMenu aria-label={t("Chat actions")}>
                                <DropdownItem
                                  key="rename"
                                  onPress={() => {
                                    setChatToRename({ id: item.id, name: item.summary || "" });
                                    setRenameModalOpen(true);
                                  }}
                                >
                                  <div className="flex items-center gap-2">
                                    <Icon icon="solar:pen-linear" width={20} />
                                    {t("Rename")}
                                  </div>
                                </DropdownItem>
                                <DropdownItem
                                  key="delete"
                                  className="text-danger"
                                  color="danger"
                                  onPress={() => {
                                    setChatToDelete(item);
                                    setDeleteModalOpen(true);
                                  }}
                                >
                                  <div className="flex items-center gap-2">
                                    <Icon icon="solar:trash-bin-trash-linear" width={20} />
                                    {t("Delete")}
                                  </div>
                                </DropdownItem>
                              </DropdownMenu>
                            </DropdownPopover>
                          </Dropdown>
                        </div>
                      </ListBoxItem>
                    ))}
                  </ListBoxSection>
                );
              })
              .filter(Boolean)}
            {/* Loading indicator at the bottom */}
            {(isLoadingMore || isLoading) && (
              <ListBoxItem
                key="loading-indicator"
                isDisabled
                className="h-[38px] px-3 py-1.5 text-default-400 pointer-events-none"
                data-id="chat-history-loading"
                id="loading-indicator"
                textValue={t("Loading more chats...")}
              >
                <div className="flex items-center justify-between w-full">
                  <span>{t("Loading more chats...")}</span>
                  <div aria-label={t("Loading")} className="animate-spin" role="status">
                    <Icon className="text-default-300" icon="solar:refresh-linear" width={20} />
                  </div>
                </div>
              </ListBoxItem>
            )}
          </Fragment>
        </ListBox>
      </ScrollShadow>

      {/* Rename Chat Modal */}
      {chatToRename && (
        <RenameChatModal
          chatId={chatToRename.id}
          currentName={chatToRename.name}
          isOpen={renameModalOpen}
          onOpenChange={(isOpen) => {
            setRenameModalOpen(isOpen);
            if (!isOpen) {
              setChatToRename(null);
            }
          }}
          onSuccess={async () => {
            // Refresh chat history to show updated name
            if (user?.id && adminClient) {
              await fetchChatHistory(user.id, adminClient);
            }
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {chatToDelete && deleteModalOpen && (
        <ModalOverlay isOpen={deleteModalOpen} size="md" onOpenChange={setDeleteModalOpen}>
          <ModalHeader className="flex flex-col gap-1">{t("Confirm Delete")}</ModalHeader>
          <ModalBody>
            <p>{t("Are you sure you want to delete this chat?")}</p>
            {chatToDelete.summary && (
              <p className="text-sm text-default-500 mt-2">"{chatToDelete.summary}"</p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onPress={() => setDeleteModalOpen(false)}>
              {t("Cancel")}
            </Button>
            <Button
              variant="danger"
              onPress={async () => {
                if (adminClient) {
                  const wasSelected = selectedChatId === chatToDelete.id;

                  await deleteChatHistory(chatToDelete.id, adminClient);
                  setChatToDelete(null);
                  setDeleteModalOpen(false);

                  // Navigate to new chat if deleted chat was currently selected
                  if (wasSelected) {
                    onNewChat(); // Use handleNewChat from parent (includes focus logic)
                  }
                }
              }}
            >
              {t("Delete")}
            </Button>
          </ModalFooter>
        </ModalOverlay>
      )}
    </div>
  );
};
